import React, { useEffect } from 'react';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import {
  Link,
  useActionData,
  useLocation,
  useNavigate,
  useSubmit,
} from 'react-router-dom';
import { Button, Container, Form } from 'react-bootstrap';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  fileUploadField,
  stringFieldRequired,
} from '@/common/utilities/formFieldSchemas';
import {
  CredentialData,
  InsuranceCompanyData,
  ProviderData,
  StateData,
} from '@/common/types';
import StateMultiSelect from '@/portal/routes/providers/components/StateMultiSelect.tsx';
import CredentialMultiSelect from '@/portal/routes/providers/components/CredentialMultiSelect.tsx';
import InsuranceMultiSelect from '@/portal/routes/providers/components/InsuranceMultiSelect.tsx';
import { objectToFormData } from '@/common/utilities/objectToFormData.ts';
import AvatarUploadField from '@/portal/routes/providers/components/AvatarUploadField.tsx';
import { DirtyFormFieldClassProvider } from '@/common/contexts/DirtyFormFieldClassContext.tsx';
import PhotoUploadField from '@/portal/routes/providers/components/PhotoUploadField.tsx';
import { useFlashMessage } from '@/common/contexts/FlashMessageContext.tsx';
import { useDirtyFieldClass } from '@/common/hooks/useDirtyFieldClass.ts';

const schema = z.object({
  firstName: stringFieldRequired('First name'),
  lastName: stringFieldRequired('Last name'),
  ehrUuid: z.string().optional(),
  bio: z.string().optional(),
  states: z.array(z.any()).optional(),
  credentials: z.array(z.any()).optional(),
  insuranceCompanies: z.array(z.any()).optional(),
  avatar: fileUploadField({ fieldName: 'Avatar', maxSizeMB: 1 }),
  photo: fileUploadField({ fieldName: 'Photo', maxSizeMB: 5 }),
});

type FormData = z.infer<typeof schema>;

interface ProviderEditFormProps {
  provider?: ProviderData;
}

const ProviderEditForm: React.FC<ProviderEditFormProps> = ({ provider }) => {
  const defaultValues = {
    firstName: provider?.firstName || '',
    lastName: provider?.lastName || '',
    ehrUuid: provider?.ehrUuid || '',
    bio: provider?.bio || '',
    states: provider?.states || [],
    credentials: provider?.credentials || [],
    insuranceCompanies: provider?.insuranceCompanies || [],
  };

  const methods = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    getValues,
    control,
    formState: { errors, dirtyFields },
  } = methods;

  // Use the dirty field class hook
  const { getDirtyClass, isDirty, getChangedFields } = useDirtyFieldClass(
    dirtyFields,
    errors,
    getValues
  );

  const submit = useSubmit();
  const location = useLocation();

  const actionData = useActionData();
  const { setFlashMessage } = useFlashMessage();
  const navigate = useNavigate();

  useEffect(() => {
    if (actionData?.success) {
      setFlashMessage(actionData.flash.message, actionData.flash.type);
      navigate(`/portal/providers/${actionData.provider.id}`);
    } else if (actionData?.success == false) {
      setFlashMessage(actionData.flash.message, actionData.flash.type);
    }
  }, [actionData, setFlashMessage, navigate]);

  const onSubmit = () => {
    const { states, credentials, insuranceCompanies, ...fieldsToSubmit } =
      getChangedFields();

    const submissionData: Record<string, unknown> = { ...fieldsToSubmit };

    if (states) {
      submissionData.stateIds = states.map((state: StateData) => state.id);
    }

    if (credentials) {
      submissionData.credentialIds = credentials.map(
        (credential: CredentialData) => credential.id
      );
    }

    if (insuranceCompanies) {
      submissionData.insuranceCompanyIds = insuranceCompanies.map(
        (insuranceCompany: InsuranceCompanyData) => insuranceCompany.id
      );
    }

    const formData = objectToFormData(submissionData, 'provider');

    void submit(formData, {
      method: provider ? 'patch' : 'post',
      action: location.pathname,
      encType: 'multipart/form-data',
    });
  };

  return (
    <FormProvider {...methods}>
      <DirtyFormFieldClassProvider
        getDirtyClass={getDirtyClass as (field: string) => string}
      >
        <Container className="py-5" style={{ maxWidth: 600 }}>
          <h2 className="mb-4">{provider ? 'Edit' : 'Create'} Provider</h2>
          <Form method="post" onSubmit={handleSubmit(onSubmit)}>
            <Form.Group className="mb-3" controlId="firstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                isInvalid={!!errors.firstName}
                className={getDirtyClass('firstName')}
                {...register('firstName')}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                isInvalid={!!errors.lastName}
                className={getDirtyClass('lastName')}
                {...register('lastName')}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="ehrUuid">
              <Form.Label>Healthie Provider ID</Form.Label>
              <Form.Control
                type="text"
                isInvalid={!!errors.ehrUuid}
                className={getDirtyClass('ehrUuid')}
                {...register('ehrUuid')}
              />
              <Form.Control.Feedback type="invalid">
                {errors.ehrUuid?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <AvatarUploadField provider={provider} />
            <PhotoUploadField provider={provider} />

            <Form.Group className="mb-3" controlId="bio">
              <Form.Label>Bio</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                className={getDirtyClass('bio')}
                {...register('bio')}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="states">
              <Controller
                name="states"
                control={control}
                defaultValue={provider?.states || []}
                render={({ field }) => (
                  <StateMultiSelect
                    selected={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="credentials">
              <Controller
                name="credentials"
                control={control}
                defaultValue={provider?.credentials || []}
                render={({ field }) => (
                  <CredentialMultiSelect
                    selected={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="insuranceCompanies">
              <Controller
                name="insuranceCompanies"
                control={control}
                defaultValue={provider?.insuranceCompanies || []}
                render={({ field }) => (
                  <InsuranceMultiSelect
                    selected={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            </Form.Group>

            <Button variant="primary" type="submit" disabled={!isDirty}>
              {provider ? 'Save' : 'Create'}
            </Button>
            <Link to={'/portal/providers'}>
              <Button variant="outline-warning" className="ms-3">
                Cancel
              </Button>
            </Link>
          </Form>
        </Container>
      </DirtyFormFieldClassProvider>
    </FormProvider>
  );
};

export default ProviderEditForm;
